import {
  Body,
  Box,
  type BoxProps,
  Center,
  Image as SystemImage,
} from '@hover/blueprint';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

import { FullscreenImageThumbnail } from 'src/modules/homeownerApp/components/FullscreenImage';
import { Image } from 'src/modules/homeownerApp/components/SharePageLayout/Image';
import { getAiGeneratedImageStyle } from 'src/modules/homeownerApp/components/SharePageLayout/Images';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { generatePath } from 'src/shared/route';
import { imageLoaded } from 'src/shared/telemetry';

interface MobileImagesProps extends BoxProps {
  aiDisplayImages: AiDisplayImage[];
  orgId?: number | null;
  fullScreenPath: string;
}

export const MobileImages = ({
  aiDisplayImages,
  orgId,
  fullScreenPath,
  ...props
}: MobileImagesProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { trackingData } = useHomeownerAppContext();
  const imagesByOriginalImageId = aiDisplayImages.reduce(
    (acc: Record<string, AiDisplayImage[]>, image) =>
      image?.aiOriginalImage?.id
        ? {
            ...acc,
            [image.aiOriginalImage.id]: acc[image.aiOriginalImage.id]
              ? [...acc[image.aiOriginalImage.id], image]
              : [image],
          }
        : acc,
    {},
  );

  useEffect(() => {
    if (aiDisplayImages?.length) {
      aiDisplayImages.forEach(image => {
        imageLoaded({
          ...trackingData,
          ai_original_image_id: image?.aiOriginalImage?.id,
          ai_generated_image_id: image?.aiGeneratedImage?.id,
          feature: 'lead-landing-page',
          page_or_screen_name: 'lead-landing-page',
          track_id: 'lead-landing-image-viewed',
          org_id: orgId ?? undefined,
        });
      });
    }
  }, [aiDisplayImages, orgId, trackingData]);

  return (
    <Box {...props} flexDirection="column">
      {Object.values(imagesByOriginalImageId).map(images =>
        images.map((image, idx) => (
          <Box flexDirection="column" key={image.id}>
            <FullscreenImageThumbnail
              image={image}
              onClick={() => {
                history.push(
                  generatePath(fullScreenPath, {
                    id,
                    aiDisplayImageId: image.id,
                  }),
                );
              }}
            >
              <Image borderRadius={400} image={image} />
            </FullscreenImageThumbnail>
            <Body
              fontWeight={700}
              margin={0}
              marginBottom={700}
              marginTop={500}
            >
              {getAiGeneratedImageStyle(t, image.aiGeneratedImage.style)}
            </Body>
            {idx === images.length - 1 && (
              <Box flexDirection="column">
                <Center
                  borderRadius={400}
                  maxWidth={{ tablet: '460px' }}
                  overflow="hidden"
                  sx={{ aspectRatio: '460 / 600' }}
                  width="100%"
                >
                  <SystemImage
                    alt="original image"
                    height="100%"
                    objectFit="cover"
                    src={images[idx].aiOriginalImage?.originalImagePath}
                    width="100%"
                  />
                </Center>
                <Body
                  fontWeight={700}
                  margin={0}
                  marginBottom={700}
                  marginTop={500}
                >
                  {t('share.original')}
                </Body>
              </Box>
            )}
          </Box>
        )),
      )}
    </Box>
  );
};
